import { useState, useEffect, useRef, useCallback } from 'react'
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Paper,
    List,
    ListItemButton,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Badge,
    Chip,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Button,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Collapse,
    InputAdornment,
} from '@mui/material'
import {
    Send,
    Chat as ChatIcon,
    Person,
    Close as CloseIcon,
    Circle,
    Forum,
    Refresh,
    InsertDriveFile,
    PlayArrow,
    Pause,
    Assessment,
    Save,
    AccessTime,
    QuestionAnswer,
    AttachFile,
    Mic,
    SupportAgent,
    Timer,
    InfoOutlined,
    SmartToy,
    OpenInNew,
    Search,
    ReplyAll,
    Add,
    Delete,
    ExpandMore,
    ExpandLess,
    Analytics,
    TrendingUp,
    Edit,
    Check,
} from '@mui/icons-material'
import { useUIStore } from '@store/uiStore'
import { chatAgentApi } from '@api/chat'
import { useAuthStore } from '@store/authStore'
import { toast } from 'react-toastify'
import formsApi from '@api/forms'

const POLL_INTERVAL = 5000
const API_URL = import.meta.env.VITE_API_URL || '/api'
const API_BASE = API_URL.replace(/\/api\/?$/, '/api')

// ── Custom waveform audio player for agent view ──
const AgentAudioPlayer = ({ src, isAgent }) => {
    const audioRef = useRef(null)
    const [playing, setPlaying] = useState(false)
    const [progress, setProgress] = useState(0)
    const [duration, setDuration] = useState(0)
    const [currentTime, setCurrentTime] = useState(0)
    const animFrame = useRef(null)

    const fmtTime = (s) => {
        if (!s || isNaN(s)) return '0:00'
        const m = Math.floor(s / 60)
        const sec = Math.floor(s % 60)
        return `${m}:${sec.toString().padStart(2, '0')}`
    }

    const toggle = () => {
        if (!audioRef.current) return
        if (playing) {
            audioRef.current.pause()
        } else {
            audioRef.current.play().catch(() => { })
        }
        setPlaying(!playing)
    }

    useEffect(() => {
        const a = audioRef.current
        if (!a) return
        const updateProgress = () => {
            setCurrentTime(a.currentTime)
            setProgress(a.duration ? (a.currentTime / a.duration) * 100 : 0)
            animFrame.current = requestAnimationFrame(updateProgress)
        }
        const onPlay = () => {
            animFrame.current = requestAnimationFrame(updateProgress)
        }
        const onPause = () => cancelAnimationFrame(animFrame.current)
        const onEnded = () => {
            setPlaying(false)
            setProgress(0)
            setCurrentTime(0)
        }
        const onLoaded = () => setDuration(a.duration)
        a.addEventListener('play', onPlay)
        a.addEventListener('pause', onPause)
        a.addEventListener('ended', onEnded)
        a.addEventListener('loadedmetadata', onLoaded)
        return () => {
            a.removeEventListener('play', onPlay)
            a.removeEventListener('pause', onPause)
            a.removeEventListener('ended', onEnded)
            a.removeEventListener('loadedmetadata', onLoaded)
            cancelAnimationFrame(animFrame.current)
        }
    }, [])

    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            minWidth: 180, maxWidth: 260,
        }}>
            <audio ref={audioRef} src={src} preload="metadata" />
            <IconButton size="small" onClick={toggle} sx={{
                color: isAgent ? '#fff' : 'primary.main',
                bgcolor: isAgent ? 'rgba(255,255,255,0.15)' : 'rgba(25,118,210,0.08)',
                '&:hover': { bgcolor: isAgent ? 'rgba(255,255,255,0.25)' : 'rgba(25,118,210,0.15)' },
                width: 32, height: 32,
            }}>
                {playing ? <Pause sx={{ fontSize: 18 }} /> : <PlayArrow sx={{ fontSize: 18 }} />}
            </IconButton>
            <Box sx={{ flex: 1 }}>
                <Box sx={{
                    height: 4, borderRadius: 2, bgcolor: isAgent ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                }}>
                    <Box sx={{
                        height: '100%', borderRadius: 2, width: `${progress}%`,
                        bgcolor: isAgent ? '#fff' : 'primary.main', transition: 'width 0.1s',
                    }} />
                </Box>
                <Typography sx={{ fontSize: 10, mt: 0.3, opacity: 0.7, color: isAgent ? '#fff' : 'text.secondary' }}>
                    {fmtTime(currentTime)} / {fmtTime(duration)}
                </Typography>
            </Box>
        </Box>
    )
}

// ── Message content renderer ──
const AgentMessageContent = ({ msg }) => {
    const isAgent = msg.sender_type === 'agent'

    if (msg.message_type === 'voice') {
        const audioUrl = msg.file_url?.startsWith('http')
            ? msg.file_url
            : `${API_BASE}${msg.file_url}`
        return <AgentAudioPlayer src={audioUrl} isAgent={isAgent} />
    }

    if (msg.message_type === 'image') {
        const imgUrl = msg.file_url?.startsWith('http')
            ? msg.file_url
            : `${API_BASE}${msg.file_url}`
        return (
            <Box>
                <img
                    src={imgUrl}
                    alt={msg.file_name || 'Image'}
                    style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, cursor: 'pointer' }}
                    onClick={() => window.open(imgUrl, '_blank')}
                />
                {msg.message && msg.message !== msg.file_name && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{msg.message}</Typography>
                )}
            </Box>
        )
    }

    if (msg.message_type === 'file') {
        const fileUrl = msg.file_url?.startsWith('http')
            ? msg.file_url
            : `${API_BASE}${msg.file_url}`
        return (
            <Box
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    cursor: 'pointer', '&:hover': { opacity: 0.8 },
                }}
                onClick={() => window.open(fileUrl, '_blank')}
            >
                <InsertDriveFile fontSize="small" />
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {msg.file_name || msg.message || 'Download file'}
                </Typography>
            </Box>
        )
    }

    const isArabicText = /[\u0600-\u06FF]/.test(msg.message)
    return (
        <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            style={isArabicText ? { direction: 'rtl', textAlign: 'right', unicodeBidi: 'embed' } : undefined}
        >
            {msg.message}
        </Typography>
    )
}

// ── Stats Info Card ──
const StatItem = ({ icon, label, value, color = 'text.primary' }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        {icon}
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>{label}</Typography>
        <Typography variant="body2" fontWeight={600} color={color}>{value ?? '—'}</Typography>
    </Box>
)

// ── Close Conversation Dialog ──
const CloseDialog = ({ open, onClose, onConfirm, loading, isRTL }) => {
    const [notes, setNotes] = useState('')
    const [saveAsSubmission, setSaveAsSubmission] = useState(true)

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloseIcon color="error" /> {isRTL ? 'إغلاق المحادثة' : 'Close Conversation'}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {isRTL ? 'أضف ملاحظات حول هذه المحادثة قبل الإغلاق:' : 'Add notes about this conversation before closing:'}
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder={isRTL ? 'ملاحظات الوكيل (النتيجة، المتابعة المطلوبة...)' : 'Agent notes (resolution, outcome, follow-up needed...)'}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 2 }}
                />
                <Box
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
                        bgcolor: saveAsSubmission ? 'primary.50' : 'grey.50',
                        borderRadius: 2, cursor: 'pointer',
                        border: '1px solid',
                        borderColor: saveAsSubmission ? 'primary.200' : 'grey.200',
                    }}
                    onClick={() => setSaveAsSubmission(!saveAsSubmission)}
                >
                    <Save color={saveAsSubmission ? 'primary' : 'disabled'} />
                    <Box>
                        <Typography variant="body2" fontWeight={600}>
                            {isRTL ? 'حفظ كطلب' : 'Save as Submission'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {isRTL ? 'أرشفة المحادثة مع الإحصائيات والنص الكامل كطلب' : 'Archive this conversation with full stats and transcript as a form submission'}
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto' }}>
                        <Chip
                            label={saveAsSubmission ? (isRTL ? 'مفعّل' : 'ON') : (isRTL ? 'معطّل' : 'OFF')}
                            size="small"
                            color={saveAsSubmission ? 'primary' : 'default'}
                            sx={{ fontWeight: 700, fontSize: 11 }}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} color="inherit">{isRTL ? 'إلغاء' : 'Cancel'}</Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={() => onConfirm(notes, saveAsSubmission)}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : <CloseIcon />}
                >
                    {isRTL ? 'إغلاق المحادثة' : 'Close Conversation'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

/**
 * LiveChat - Agent Dashboard Page
 * Left panel: conversation list
 * Right panel: active conversation messages + stats
 */
const LiveChat = () => {
    const { user } = useAuthStore()
    const { language } = useUIStore()
    const isRTL = language === 'ar'
    const [forms, setForms] = useState([])
    const [selectedFormId, setSelectedFormId] = useState(null)
    const [conversations, setConversations] = useState([])
    const [activeConv, setActiveConv] = useState(null)
    const [loading, setLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState('all')
    const [messages, setMessages] = useState([])
    const [newMessage, setNewMessage] = useState('')
    const [sending, setSending] = useState(false)
    const messagesEndRef = useRef(null)
    const pollRef = useRef(null)

    // Stats & close dialog state
    const [showStats, setShowStats] = useState(false)
    const [stats, setStats] = useState(null)
    const [statsLoading, setStatsLoading] = useState(false)
    const [closeDialogOpen, setCloseDialogOpen] = useState(false)
    const [closeLoading, setCloseLoading] = useState(false)

    // Canned responses
    const [cannedResponses, setCannedResponses] = useState(() => {
        try { return JSON.parse(localStorage.getItem('chat_canned_responses') || '[]') } catch { return [] }
    })
    const [showCannedPanel, setShowCannedPanel] = useState(false)
    const [newCannedTitle, setNewCannedTitle] = useState('')
    const [newCannedText, setNewCannedText] = useState('')

    // Analytics
    const [showAnalytics, setShowAnalytics] = useState(false)

    // Transcript search
    const [transcriptSearch, setTranscriptSearch] = useState('')

    // Typing indicator
    const [visitorTyping, setVisitorTyping] = useState(false)
    const typingTimeoutRef = useRef(null)

    // Edit message
    const [editingMessageId, setEditingMessageId] = useState(null)
    const [editingText, setEditingText] = useState('')

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    // Load forms that have live chat enabled
    useEffect(() => {
        const loadForms = async () => {
            try {
                const res = await formsApi.getAll()
                const chatForms = (res.data || []).filter(f => f.enable_live_chat)
                setForms(chatForms)
                if (chatForms.length > 0 && !selectedFormId) {
                    setSelectedFormId(chatForms[0].id)
                }
            } catch (err) {
                console.error('Failed to load forms:', err)
            }
        }
        loadForms()
    }, [])

    // Load conversations
    const loadConversations = useCallback(async () => {
        if (!selectedFormId) return
        try {
            const res = await chatAgentApi.getConversations(selectedFormId, statusFilter)
            setConversations(res.data.conversations || res.data || [])
            setLoading(false)
        } catch (err) {
            console.error('Failed to load conversations:', err)
            setLoading(false)
        }
    }, [selectedFormId, statusFilter])

    useEffect(() => {
        setLoading(true)
        loadConversations()
    }, [selectedFormId, statusFilter])

    // Load messages for active conversation
    const loadMessages = useCallback(async (fullReload = false) => {
        if (!activeConv) return
        try {
            const res = await chatAgentApi.getMessages(activeConv.id)
            const serverMsgs = res.data.messages || res.data || []
            setMessages(serverMsgs)
            setTimeout(scrollToBottom, 100)
        } catch (err) {
            console.error('Failed to load messages:', err)
        }
    }, [activeConv?.id])

    // When active conversation changes, load messages
    useEffect(() => {
        if (activeConv) {
            loadMessages(true)
            setStats(null) // Reset stats when switching conversations
            setShowStats(false)
        }
    }, [activeConv?.id])

    // Polling for new messages and conversations
    useEffect(() => {
        pollRef.current = setInterval(() => {
            if (activeConv) loadMessages()
            loadConversations()
        }, POLL_INTERVAL)
        return () => clearInterval(pollRef.current)
    }, [activeConv?.id, loadMessages, loadConversations])

    // Send message
    const handleSend = async () => {
        if (!newMessage.trim() || sending || !activeConv) return
        const msgText = newMessage.trim()
        setSending(true)
        setNewMessage('')
        try {
            await chatAgentApi.sendMessage(activeConv.id, msgText)
            await loadMessages()
        } catch (err) {
            toast.error('Failed to send message')
            setNewMessage(msgText)
        } finally {
            setSending(false)
        }
    }

    // Edit message handlers
    const handleEditMessage = (msg) => {
        setEditingMessageId(msg.id)
        setEditingText(msg.message)
    }

    const handleSaveEdit = async () => {
        if (!editingText.trim() || !activeConv || !editingMessageId) return
        try {
            await chatAgentApi.editMessage(activeConv.id, editingMessageId, editingText.trim())
            setEditingMessageId(null)
            setEditingText('')
            await loadMessages()
        } catch (err) {
            toast.error('Failed to edit message')
        }
    }

    const handleCancelEdit = () => {
        setEditingMessageId(null)
        setEditingText('')
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    // Close with dialog
    const handleCloseClick = () => {
        setCloseDialogOpen(true)
    }

    const handleCloseConfirm = async (notes, saveAsSubmission) => {
        setCloseLoading(true)
        try {
            await chatAgentApi.closeConversation(activeConv.id, notes || null)

            if (saveAsSubmission) {
                try {
                    const saveRes = await chatAgentApi.saveAsSubmission(activeConv.id)
                    toast.success(`Conversation closed & saved as ${saveRes.data?.submission_id || 'submission'}`)
                } catch (saveErr) {
                    toast.warning('Conversation closed but failed to save as submission')
                    console.error('Save as submission failed:', saveErr)
                }
            } else {
                toast.success('Conversation closed')
            }

            setCloseDialogOpen(false)
            setActiveConv(null)
            setMessages([])
            loadConversations()
        } catch (err) {
            toast.error('Failed to close conversation')
        } finally {
            setCloseLoading(false)
        }
    }

    // Load stats for active conversation
    const handleToggleStats = async () => {
        if (showStats) {
            setShowStats(false)
            return
        }
        if (!activeConv) return
        setStatsLoading(true)
        try {
            const res = await chatAgentApi.getConversationStats(activeConv.id)
            setStats(res.data)
            setShowStats(true)
        } catch (err) {
            toast.error('Failed to load stats')
        } finally {
            setStatsLoading(false)
        }
    }

    // Save as submission (for already-closed conversations)
    const handleSaveAsSubmission = async () => {
        if (!activeConv) return
        try {
            const res = await chatAgentApi.saveAsSubmission(activeConv.id)
            toast.success(`Saved as ${res.data?.submission_id || 'submission'}`)
            // Refresh stats to show linked submission
            const statsRes = await chatAgentApi.getConversationStats(activeConv.id)
            setStats(statsRes.data)
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to save as submission')
        }
    }

    const formatTime = (dateStr) => {
        if (!dateStr) return ''
        const d = new Date(dateStr)
        const now = new Date()
        const isToday = d.toDateString() === now.toDateString()
        return isToday
            ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : d.toLocaleDateString([], { month: 'short', day: 'numeric' })
    }

    // Save canned response
    const saveCannedResponse = () => {
        if (!newCannedTitle.trim() || !newCannedText.trim()) return
        const updated = [...cannedResponses, { title: newCannedTitle.trim(), text: newCannedText.trim(), id: Date.now() }]
        setCannedResponses(updated)
        localStorage.setItem('chat_canned_responses', JSON.stringify(updated))
        setNewCannedTitle('')
        setNewCannedText('')
        toast.success(isRTL ? 'تم حفظ الرد السريع' : 'Quick reply saved')
    }

    const deleteCannedResponse = (id) => {
        const updated = cannedResponses.filter(r => r.id !== id)
        setCannedResponses(updated)
        localStorage.setItem('chat_canned_responses', JSON.stringify(updated))
    }

    const useCannedResponse = (text) => {
        setNewMessage(text)
        setShowCannedPanel(false)
    }

    // Compute analytics from conversations
    const chatAnalytics = (() => {
        if (!conversations.length) return null
        const total = conversations.length
        const active = conversations.filter(c => c.status === 'active').length
        const closed = conversations.filter(c => c.status === 'closed').length
        const withRating = conversations.filter(c => c.visitor_rating > 0)
        const avgRating = withRating.length ? (withRating.reduce((sum, c) => sum + c.visitor_rating, 0) / withRating.length).toFixed(1) : '—'
        const totalMessages = conversations.reduce((sum, c) => sum + (c.message_count || 0), 0)
        return { total, active, closed, avgRating, totalMessages }
    })()

    // Filtered messages for transcript search
    const filteredMessages = transcriptSearch
        ? messages.filter(m => m.message?.toLowerCase().includes(transcriptSearch.toLowerCase()))
        : messages

    // Simulate typing indicator (based on poll — check latest visitor message)
    useEffect(() => {
        if (messages.length > 0) {
            const lastMsg = messages[messages.length - 1]
            if (lastMsg.sender_type === 'visitor') {
                const msgTime = new Date(lastMsg.created_at).getTime()
                const now = Date.now()
                if (now - msgTime < 3000) {
                    setVisitorTyping(true)
                    clearTimeout(typingTimeoutRef.current)
                    typingTimeoutRef.current = setTimeout(() => setVisitorTyping(false), 3000)
                }
            }
        }
    }, [messages.length])

    return (
        <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Forum sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Typography variant="h5" fontWeight={700}>{isRTL ? 'المحادثة المباشرة' : 'Live Chat'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {forms.length > 1 && (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>{isRTL ? 'النموذج' : 'Form'}</InputLabel>
                            <Select
                                value={selectedFormId || ''}
                                label={isRTL ? 'النموذج' : 'Form'}
                                onChange={(e) => {
                                    setSelectedFormId(e.target.value)
                                    setActiveConv(null)
                                    setMessages([])
                                }}
                            >
                                {forms.map(f => (
                                    <MenuItem key={f.id} value={f.id}>{f.title}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>{isRTL ? 'الحالة' : 'Status'}</InputLabel>
                        <Select
                            value={statusFilter}
                            label={isRTL ? 'الحالة' : 'Status'}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <MenuItem value="all">{isRTL ? 'الكل' : 'All'}</MenuItem>
                            <MenuItem value="active">{isRTL ? 'نشط' : 'Active'}</MenuItem>
                            <MenuItem value="closed">{isRTL ? 'مغلق' : 'Closed'}</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title={isRTL ? 'تحديث' : 'Refresh'}>
                        <IconButton onClick={loadConversations} size="small">
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={isRTL ? 'إحصائيات' : 'Analytics'}>
                        <IconButton onClick={() => setShowAnalytics(!showAnalytics)} color={showAnalytics ? 'primary' : 'default'} size="small">
                            <Analytics />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Analytics Panel */}
            <Collapse in={showAnalytics}>
                {chatAnalytics && (
                    <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }} elevation={1}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            <TrendingUp sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                            {isRTL ? 'إحصائيات المحادثات' : 'Chat Analytics'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {[
                                { label: isRTL ? 'الإجمالي' : 'Total', value: chatAnalytics.total, color: 'primary.main' },
                                { label: isRTL ? 'نشط' : 'Active', value: chatAnalytics.active, color: 'success.main' },
                                { label: isRTL ? 'مغلق' : 'Closed', value: chatAnalytics.closed, color: 'text.secondary' },
                                { label: isRTL ? 'متوسط التقييم' : 'Avg Rating', value: chatAnalytics.avgRating, color: 'warning.main' },
                                { label: isRTL ? 'إجمالي الرسائل' : 'Messages', value: chatAnalytics.totalMessages, color: 'info.main' },
                            ].map((stat, idx) => (
                                <Box key={idx} sx={{ textAlign: 'center', minWidth: 80 }}>
                                    <Typography variant="h6" fontWeight={700} color={stat.color}>{stat.value}</Typography>
                                    <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                )}
            </Collapse>

            {/* Main Content */}
            <Paper
                elevation={3}
                sx={{
                    flex: 1,
                    display: 'flex',
                    borderRadius: 3,
                    overflow: 'hidden',
                    minHeight: 0,
                }}
            >
                {/* Conversation List */}
                <Box sx={{
                    width: 340,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#fafafa',
                }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            {isRTL ? `${conversations.length} محادثة` : `${conversations.length} conversation${conversations.length !== 1 ? 's' : ''}`}
                        </Typography>
                    </Box>
                    <List sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : conversations.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <ChatIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                    {isRTL ? 'لا توجد محادثات بعد' : 'No conversations yet'}
                                </Typography>
                            </Box>
                        ) : (
                            conversations.map((conv) => (
                                <ListItemButton
                                    key={conv.id}
                                    selected={activeConv?.id === conv.id}
                                    onClick={() => setActiveConv(conv)}
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        '&.Mui-selected': { bgcolor: 'primary.50' },
                                    }}
                                >
                                    <ListItemAvatar>
                                        <Badge
                                            badgeContent={conv.unread_count > 0 ? conv.unread_count : null}
                                            color="error"
                                            overlap="circular"
                                        >
                                            <Avatar sx={{
                                                bgcolor: conv.status === 'active' ? 'primary.main' : 'grey.400',
                                                width: 40,
                                                height: 40,
                                            }}>
                                                <Person fontSize="small" />
                                            </Avatar>
                                        </Badge>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="subtitle2" fontWeight={conv.unread_count > 0 ? 700 : 500} noWrap>
                                                    {conv.visitor_name || 'Visitor'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
                                                    {formatTime(conv.last_message_at || conv.created_at)}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Circle sx={{
                                                    fontSize: 8,
                                                    color: conv.status === 'active' ? 'success.main' : 'grey.400',
                                                }} />
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    noWrap
                                                    sx={{ fontWeight: conv.unread_count > 0 ? 600 : 400 }}
                                                >
                                                    {conv.last_message || 'No messages'}
                                                </Typography>
                                                {conv.total_messages > 0 && (
                                                    <Chip
                                                        label={conv.total_messages}
                                                        size="small"
                                                        sx={{ height: 16, fontSize: 9, ml: 'auto', flexShrink: 0 }}
                                                    />
                                                )}
                                            </Box>
                                        }
                                    />
                                </ListItemButton>
                            ))
                        )}
                    </List>
                </Box>

                {/* Chat Panel */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    {activeConv ? (
                        <>
                            {/* Chat Header */}
                            <Box sx={{
                                p: 2,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                bgcolor: '#fff',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                                        <Person fontSize="small" />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {activeConv.visitor_name || (isRTL ? 'زائر' : 'Visitor')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {activeConv.visitor_email || (isRTL ? 'بدون بريد' : 'No email')}
                                            {' · '}
                                            <Chip
                                                label={activeConv.status}
                                                size="small"
                                                color={activeConv.status === 'active' ? 'success' : 'default'}
                                                sx={{ height: 18, fontSize: 10 }}
                                            />
                                            {activeConv.status === 'closed' && activeConv.closed_by_name && (
                                                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                                                    · {isRTL ? `أغلق بواسطة ${activeConv.closed_by_name}` : `Closed by ${activeConv.closed_by_name}`}
                                                </Typography>
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title={showStats ? (isRTL ? 'إخفاء الإحصائيات' : 'Hide stats') : (isRTL ? 'عرض الإحصائيات' : 'View stats')}>
                                        <IconButton
                                            size="small"
                                            onClick={handleToggleStats}
                                            color={showStats ? 'primary' : 'default'}
                                        >
                                            {statsLoading ? <CircularProgress size={18} /> : <Assessment fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                    {activeConv.status === 'closed' && !activeConv.submission_id && (
                                        <Tooltip title={isRTL ? 'حفظ كطلب' : 'Save as submission'}>
                                            <IconButton size="small" color="primary" onClick={handleSaveAsSubmission}>
                                                <Save fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {activeConv.status === 'active' && (
                                        <Tooltip title={isRTL ? 'إغلاق المحادثة' : 'Close conversation'}>
                                            <IconButton
                                                color="error"
                                                size="small"
                                                onClick={handleCloseClick}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>

                            {/* Stats Panel (collapsible) */}
                            {showStats && stats && (
                                <Box sx={{
                                    p: 2,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: '#f0f4ff',
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 0.5,
                                }}>
                                    <StatItem
                                        icon={<AccessTime sx={{ fontSize: 16, color: 'primary.main' }} />}
                                        label={isRTL ? 'المدة' : 'Duration'}
                                        value={stats.duration_formatted}
                                    />
                                    <StatItem
                                        icon={<Timer sx={{ fontSize: 16, color: 'warning.main' }} />}
                                        label={isRTL ? 'أول رد' : '1st Response'}
                                        value={stats.first_response_formatted || (isRTL ? 'غير متوفر' : 'N/A')}
                                        color={stats.first_response_seconds && stats.first_response_seconds < 60 ? 'success.main' : 'warning.main'}
                                    />
                                    <StatItem
                                        icon={<QuestionAnswer sx={{ fontSize: 16, color: 'info.main' }} />}
                                        label={isRTL ? 'الرسائل' : 'Messages'}
                                        value={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                <span>{stats.total_messages}</span>
                                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, color: 'info.main' }}>
                                                    (<Person sx={{ fontSize: 14 }} />{stats.visitor_messages}
                                                    <Box component="span" sx={{ mx: 0.2 }}>·</Box>
                                                    <SmartToy sx={{ fontSize: 14 }} />{stats.agent_messages})
                                                </Box>
                                            </Box>
                                        }
                                    />
                                    <StatItem
                                        icon={<AttachFile sx={{ fontSize: 16, color: 'secondary.main' }} />}
                                        label={isRTL ? 'المرفقات' : 'Attachments'}
                                        value={isRTL
                                            ? `${stats.attachment_count} ملف · ${stats.voice_count} صوت`
                                            : `${stats.attachment_count} files · ${stats.voice_count} voice`
                                        }
                                    />
                                    <StatItem
                                        icon={<SupportAgent sx={{ fontSize: 16, color: 'success.main' }} />}
                                        label={isRTL ? 'الوكلاء' : 'Agents'}
                                        value={(stats.participating_agents || []).map(a => isRTL ? (a.name_ar || a.name_en) : (a.name_en || a.name_ar)).join(', ') || (isRTL ? 'لا يوجد' : 'None')}
                                    />
                                    {stats.agent_notes && (
                                        <Box sx={{ gridColumn: '1 / -1', mt: 0.5, pt: 1, borderTop: '1px solid', borderColor: 'primary.100' }}>
                                            <StatItem
                                                icon={<InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />}
                                                label={isRTL ? 'ملاحظات' : 'Notes'}
                                                value={stats.agent_notes}
                                            />
                                        </Box>
                                    )}
                                    {stats.submission_id && (
                                        <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                                label={isRTL ? 'محفوظ كطلب' : 'Saved as submission'}
                                                color="success"
                                                size="small"
                                                icon={<Save />}
                                                sx={{ fontSize: 11 }}
                                            />
                                            <Button
                                                size="small"
                                                variant="text"
                                                endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                                                onClick={() => window.open(`/submissions/${stats.submission_id}`, '_self')}
                                                sx={{ fontSize: 11, textTransform: 'none', ml: 'auto' }}
                                            >
                                                {isRTL ? 'عرض في الطلبات' : 'View in Submissions'}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Messages */}
                            <Box sx={{
                                flex: 1,
                                overflowY: 'auto',
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                bgcolor: 'action.hover',
                            }}>
                                {filteredMessages.map((msg, idx) => (
                                    <Box
                                        key={msg.id || idx}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: msg.sender_type === 'agent' ? 'flex-end' : 'flex-start',
                                        }}
                                    >
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                px: 2,
                                                py: 1,
                                                maxWidth: '70%',
                                                borderRadius: msg.sender_type === 'agent'
                                                    ? '16px 16px 4px 16px'
                                                    : '16px 16px 16px 4px',
                                                bgcolor: msg.sender_type === 'agent' ? 'primary.main' : '#fff',
                                                color: msg.sender_type === 'agent' ? '#fff' : 'text.primary',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                            }}
                                            style={/[\u0600-\u06FF]/.test(msg.message || '') ? { direction: 'rtl', textAlign: 'right' } : undefined}
                                        >
                                            {/* Agent name badge */}
                                            {msg.sender_type === 'agent' && msg.sender_name && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: 'block',
                                                        mb: 0.3,
                                                        opacity: 0.85,
                                                        fontWeight: 700,
                                                        fontSize: 10,
                                                        color: 'rgba(255,255,255,0.9)',
                                                    }}
                                                >
                                                    {msg.sender_name}
                                                </Typography>
                                            )}
                                            <AgentMessageContent msg={msg} />
                                            {msg.is_edited == 1 && (
                                                <Typography variant="caption" sx={{
                                                    display: 'inline',
                                                    opacity: 0.6,
                                                    fontStyle: 'italic',
                                                    fontSize: 9,
                                                    ml: 0.5,
                                                }}>
                                                    {isRTL ? '(تم التعديل)' : '(edited)'}
                                                </Typography>
                                            )}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        opacity: 0.7,
                                                        fontSize: 10,
                                                    }}
                                                >
                                                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </Typography>
                                                {msg.sender_type === 'agent' && (!msg.message_type || msg.message_type === 'text') && editingMessageId !== msg.id && (
                                                    <Tooltip title={isRTL ? 'تعديل' : 'Edit'}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); handleEditMessage(msg) }}
                                                            sx={{ p: 0.2, ml: 0.5, color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}
                                                        >
                                                            <Edit sx={{ fontSize: 13 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            {editingMessageId === msg.id && (
                                                <Box sx={{ mt: 1 }}>
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        size="small"
                                                        value={editingText}
                                                        onChange={(e) => setEditingText(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit() }
                                                            if (e.key === 'Escape') handleCancelEdit()
                                                        }}
                                                        autoFocus
                                                        sx={{
                                                            '& .MuiInputBase-root': {
                                                                bgcolor: 'rgba(255,255,255,0.15)',
                                                                color: '#fff',
                                                                fontSize: 13,
                                                            },
                                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                                                        }}
                                                    />
                                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: 'flex-end' }}>
                                                        <IconButton size="small" onClick={handleCancelEdit} sx={{ color: 'rgba(255,255,255,0.7)', p: 0.3 }}>
                                                            <CloseIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={handleSaveEdit} sx={{ color: '#4caf50', p: 0.3 }}>
                                                            <Check sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Paper>
                                    </Box>
                                ))}
                                <div ref={messagesEndRef} />
                            </Box>

                            {/* Input */}
                            {activeConv.status === 'active' && (
                                <Box sx={{
                                    p: 1.5,
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                }}>
                                    {/* Typing Indicator */}
                                    {visitorTyping && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, px: 1 }}>
                                            <Box sx={{ display: 'flex', gap: 0.3 }}>
                                                {[0, 1, 2].map(i => (
                                                    <Box key={i} sx={{
                                                        width: 6, height: 6, borderRadius: '50%', bgcolor: 'grey.400',
                                                        animation: 'bounce 1.4s infinite ease-in-out',
                                                        animationDelay: `${i * 0.16}s`,
                                                        '@keyframes bounce': {
                                                            '0%, 80%, 100%': { transform: 'scale(0)' },
                                                            '40%': { transform: 'scale(1)' },
                                                        },
                                                    }} />
                                                ))}
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">
                                                {isRTL ? 'الزائر يكتب...' : 'Visitor is typing...'}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Canned Responses Toolbar */}
                                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1, alignItems: 'center' }}>
                                        <Tooltip title={isRTL ? 'ردود سريعة' : 'Quick Replies'}>
                                            <IconButton size="small" onClick={() => setShowCannedPanel(!showCannedPanel)} color={showCannedPanel ? 'primary' : 'default'}>
                                                <ReplyAll fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={isRTL ? 'بحث في المحادثة' : 'Search Messages'}>
                                            <IconButton size="small" onClick={() => setTranscriptSearch(transcriptSearch ? '' : ' ')}>
                                                <Search fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {transcriptSearch !== '' && (
                                            <TextField
                                                size="small"
                                                placeholder={isRTL ? 'بحث...' : 'Search...'}
                                                value={transcriptSearch}
                                                onChange={(e) => setTranscriptSearch(e.target.value)}
                                                sx={{ flex: 1, maxWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2, height: 32 } }}
                                            />
                                        )}
                                    </Box>

                                    {/* Canned Responses Panel */}
                                    <Collapse in={showCannedPanel}>
                                        <Paper variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2, maxHeight: 200, overflowY: 'auto' }}>
                                            <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                                                {isRTL ? 'الردود السريعة' : 'Quick Replies'}
                                            </Typography>
                                            {cannedResponses.map(r => (
                                                <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                    <Chip
                                                        label={r.title}
                                                        size="small"
                                                        onClick={() => useCannedResponse(r.text)}
                                                        variant="outlined"
                                                        color="primary"
                                                        sx={{ cursor: 'pointer' }}
                                                    />
                                                    <IconButton size="small" onClick={() => deleteCannedResponse(r.id)} sx={{ p: 0.25 }}>
                                                        <Delete sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                            <Divider sx={{ my: 1 }} />
                                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                                <TextField
                                                    size="small"
                                                    placeholder={isRTL ? 'العنوان' : 'Title'}
                                                    value={newCannedTitle}
                                                    onChange={(e) => setNewCannedTitle(e.target.value)}
                                                    sx={{ width: 80, '& .MuiOutlinedInput-root': { height: 28, fontSize: 12 } }}
                                                />
                                                <TextField
                                                    size="small"
                                                    placeholder={isRTL ? 'نص الرد' : 'Reply text'}
                                                    value={newCannedText}
                                                    onChange={(e) => setNewCannedText(e.target.value)}
                                                    sx={{ flex: 1, '& .MuiOutlinedInput-root': { height: 28, fontSize: 12 } }}
                                                />
                                                <IconButton size="small" onClick={saveCannedResponse} color="primary" disabled={!newCannedTitle.trim() || !newCannedText.trim()}>
                                                    <Add fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Paper>
                                    </Collapse>

                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                        <TextField
                                            fullWidth
                                            placeholder={isRTL ? 'اكتب رداً...' : 'Type a reply...'}
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            multiline
                                            maxRows={3}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 3,
                                                    bgcolor: 'action.hover',
                                                },
                                            }}
                                        />
                                        <IconButton
                                            onClick={handleSend}
                                            disabled={!newMessage.trim() || sending}
                                            sx={{
                                                bgcolor: 'primary.main',
                                                color: '#fff',
                                                '&:hover': { bgcolor: 'primary.dark' },
                                                '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' },
                                                width: 44,
                                                height: 44,
                                            }}
                                        >
                                            <Send fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            )}
                        </>
                    ) : (
                        /* Empty state */
                        <Box sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: 'text.secondary',
                        }}>
                            <Forum sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                {isRTL ? 'اختر محادثة' : 'Select a conversation'}
                            </Typography>
                            <Typography variant="body2" color="text.disabled">
                                {isRTL ? 'اختر محادثة من اللوحة الجانبية لبدء الدردشة' : 'Choose a conversation from the left panel to start chatting'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Close Dialog */}
            <CloseDialog
                open={closeDialogOpen}
                onClose={() => setCloseDialogOpen(false)}
                onConfirm={handleCloseConfirm}
                loading={closeLoading}
                isRTL={isRTL}
            />
        </Box>
    )
}

export default LiveChat
